import { Database, Inbox, Send, Sparkles, Trash2 } from "lucide-react";
import type { AccountMailStatsDto, MailStatsDto } from "../../../shared/types";

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function since(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

/** Overall totals box: one wide card summarizing every account's local store. */
export function OverallMailStats({ stats }: { stats: MailStatsDto }) {
  const t = stats.totals;
  if (!t.total) return null;
  const items: { label: string; value: string }[] = [
    { label: "emails stored", value: t.total.toLocaleString() },
    { label: "unread", value: t.unread.toLocaleString() },
    { label: "sent", value: t.sent.toLocaleString() },
    { label: "storage used", value: formatBytes(t.summaryBytes + t.bodyBytes) },
    { label: "bodies cached", value: t.bodiesCached.toLocaleString() },
    { label: "AI-labeled", value: t.labeled.toLocaleString() },
  ];
  return (
    <div className="mb-5 rounded-lg border border-stroke bg-white p-4">
      <p className="pb-2 text-[12px] font-semibold uppercase tracking-wide text-ink-muted">
        All accounts — stored mail (last 12 months)
      </p>
      <div className="flex flex-wrap gap-x-8 gap-y-3">
        {items.map((item) => (
          <div key={item.label}>
            <p className="text-[20px] font-semibold leading-6">{item.value}</p>
            <p className="text-[11px] text-ink-muted">{item.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Compact per-account store statistics for an AccountCard. */
export function AccountMailStats({ stats }: { stats: AccountMailStatsDto | undefined }) {
  if (!stats || !stats.total) return null;
  const k = stats.byKind;
  const coverage = since(stats.oldest);
  return (
    <div className="mt-2 border-t border-stroke pt-2 text-[12px] text-ink-muted">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="flex items-center gap-1" title="Inbox (unread)">
          <Inbox size={12} /> {(k.inbox ?? 0).toLocaleString()}
          {stats.unread > 0 && <span className="font-semibold text-accent">({stats.unread})</span>}
        </span>
        <span className="flex items-center gap-1" title="Sent">
          <Send size={12} /> {(k.sent ?? 0).toLocaleString()}
        </span>
        {(k.deleted ?? 0) > 0 && (
          <span className="flex items-center gap-1" title="Deleted">
            <Trash2 size={12} /> {k.deleted!.toLocaleString()}
          </span>
        )}
        {stats.labeled > 0 && (
          <span className="flex items-center gap-1" title="AI-labeled emails">
            <Sparkles size={12} /> {stats.labeled.toLocaleString()}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 pt-1" title="Local storage: summaries + cached bodies">
        <Database size={12} />
        {stats.total.toLocaleString()} emails · {formatBytes(stats.summaryBytes + stats.bodyBytes)}
        {stats.bodiesCached > 0 && ` (${stats.bodiesCached} bodies)`}
        {coverage && <span className="text-ink-muted/70"> · since {coverage}</span>}
      </div>
    </div>
  );
}
